/**
 * Recovery for stale-bundle chunk failures after a deploy.
 *
 * Deploys flip a symlink, so the previous build's hashed chunk files stop
 * being served the moment a deploy lands. A tab left open across a deploy
 * still runs the old entry bundle, and the next route it lazy-loads 404s —
 * with no error boundary in the app, React unmounts everything and the
 * client sees a white screen. One full reload fetches the new index.html
 * and fixes it, so do that for them.
 *
 * The hard reload is reserved for a dead app: the event type hints at it
 * (window 'error' is the render path, where React 16 unmounts everything),
 * but code outside React — GrapesJS callbacks, a rethrow in a setTimeout —
 * also surfaces on window 'error' with the app still mounted, so the real
 * gate is #root itself. Only when it's empty can a reload lose nothing.
 * Everything else — an import() rejected outside render, any chunk error
 * with a live tree — gets the refresh offer in an overlay instead.
 *
 * Reloading is also guarded two ways against loops: a module-level flag so
 * a second chunk error in the same tick (a route's JS and CSS failing
 * together) can't paint the notice while the reload is in flight, and a
 * timestamp in sessionStorage so a chunk failing again shortly after an
 * automatic reload (deploy still in progress, server trouble) shows the
 * notice instead of reloading forever.
 */

const RELOAD_AT_KEY = 'continuumChunkReloadAt';
const RELOAD_LOOP_WINDOW_MS = 2 * 60 * 1000;
// A reload tears the page down well inside this. Still running when it
// fires means the reload never happened — a beforeunload handler (unsaved
// work, see usePrompt) opened a dialog and the user clicked Cancel.
const RELOAD_ABORTED_RESET_MS = 5 * 1000;
// After "Not now", how long further failures stay quiet while the app still
// works, so a route retrying a dead preload can't nag on every attempt.
const NOTICE_SNOOZE_MS = 5 * 60 * 1000;
const NOTICE_ID = 'continuum-chunk-refresh-notice';

// True once this page has initiated an automatic reload. Errors that land
// after that are logged and swallowed, nothing more.
let reloading = false;

// When the user last dismissed the notice with "Not now".
let noticeDismissedAt = 0;

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, code, message } = error as { name?: string; code?: string; message?: string };
  if (name === 'ChunkLoadError' || code === 'CSS_CHUNK_LOAD_FAILED') return true;
  // Anchored to webpack's exact shape — "Loading chunk 42 failed." /
  // "Loading CSS chunk main failed." with a one-token chunk id — so an app
  // error that merely mentions chunks can't trigger a reload.
  return typeof message === 'string' && /^Loading (CSS )?chunk \S+ failed/.test(message);
}

function lastAutoReloadAt(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
  } catch (_) {
    // Storage blocked: without the guard we can't rule out a reload loop,
    // so pretend we just reloaded and fall through to the notice.
    return Date.now();
  }
}

function markAutoReload(): void {
  try {
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch (_) {
    // Covered by lastAutoReloadAt's fallback.
  }
}

/**
 * True while React still owns a tree under #root. On an unhandled render
 * error React 16 unmounts everything, leaving #root empty — the one state
 * where an automatic reload can't lose any work.
 */
function isAppMounted(): boolean {
  const root = document.getElementById('root');
  return !!root && root.hasChildNodes();
}

/**
 * Show the refresh notice in an overlay appended to <body>, leaving #root
 * alone: React may still own that tree, and replacing its innerHTML under a
 * mounted root makes the next commit throw on detached nodes.
 *
 * "Not now" only exists while the app is mounted: over an unmounted (blank)
 * page, dismissing the notice would strand the user with nothing to click.
 */
function renderRefreshNotice(withNotNow: boolean): void {
  if (document.getElementById(NOTICE_ID)) return;
  const overlay = document.createElement('div');
  overlay.id = NOTICE_ID;
  overlay.setAttribute(
    'style',
    'position:fixed;top:0;right:0;bottom:0;left:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;' +
      "background:rgba(24,39,55,0.45);font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;"
  );
  const box = document.createElement('div');
  box.setAttribute(
    'style',
    'max-width:420px;padding:40px;background:#fff;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.1);text-align:center;'
  );
  box.innerHTML = `
    <h1 style="font-size:20px;margin:0 0 12px;color:#182737;">Continuum has been updated</h1>
    <p style="font-size:14px;line-height:1.6;color:#555;margin:0 0 24px;">
      This page needs to be refreshed to load the latest version.
      If this message keeps appearing, please close this tab and open Continuum again.
    </p>`;

  // Real listeners, not inline onclick attributes: a script-src CSP without
  // 'unsafe-inline' silently disables those, and the notice would become an
  // undismissable dead overlay.
  const refresh = document.createElement('button');
  refresh.textContent = 'Refresh';
  refresh.setAttribute(
    'style',
    'font-size:14px;padding:8px 24px;border:none;border-radius:4px;background:#1890ff;color:#fff;cursor:pointer;'
  );
  refresh.addEventListener('click', () => window.location.reload());
  box.appendChild(refresh);

  if (withNotNow) {
    const dismiss = document.createElement('button');
    dismiss.textContent = 'Not now';
    dismiss.setAttribute(
      'style',
      'font-size:14px;padding:8px 24px;margin-left:8px;border:1px solid #d9d9d9;border-radius:4px;background:#fff;color:#555;cursor:pointer;'
    );
    dismiss.addEventListener('click', () => {
      noticeDismissedAt = Date.now();
      overlay.remove();
    });
    box.appendChild(dismiss);
  }

  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

/**
 * Recover from a stale-chunk error. `canHardReload` says the event type
 * allows a reload (the render path), but the deciding check is #root: only
 * an unmounted app has nothing to lose. Returns true when the error was ours.
 */
export function handleChunkLoadError(error: unknown, canHardReload = true): boolean {
  if (!isChunkLoadError(error)) return false;

  // The listeners preventDefault(), which cancels the browser's own
  // uncaught-error logging — keep the evidence for support.
  console.error('Chunk failed to load (stale bundle after a deploy?):', error);

  if (reloading) return true;

  const mounted = isAppMounted();

  if (canHardReload && !mounted && Date.now() - lastAutoReloadAt() > RELOAD_LOOP_WINDOW_MS) {
    reloading = true;
    markAutoReload();
    window.location.reload();
    // reload() isn't guaranteed to finish: with unsaved work a beforeunload
    // handler opens the leave-site dialog, and Cancel keeps the page. Drop
    // the latch shortly after so later chunk errors still get the notice
    // instead of being swallowed forever.
    window.setTimeout(() => {
      reloading = false;
    }, RELOAD_ABORTED_RESET_MS);
  } else if (mounted && Date.now() - noticeDismissedAt < NOTICE_SNOOZE_MS) {
    // The user just clicked "Not now" to finish what they were doing; while
    // the app still works, don't repaint the overlay on every retry.
  } else {
    renderRefreshNotice(mounted);
  }
  return true;
}

/**
 * Catch chunk failures from every lazy import in the app. Loadable rethrows
 * a failed import during render, which surfaces on window as an uncaught
 * error after React 16 has unmounted the tree; imports rejected outside
 * render surface as unhandled rejections while the app is still mounted.
 */
export function installChunkLoadRecovery(): void {
  // The entry module can execute twice (see the boot-banner guard in
  // index.tsx); a doubled handler would paint the notice over the screen in
  // the moment before the first handler's reload lands.
  if ((window as any).__continuumChunkRecoveryInstalled) return;
  (window as any).__continuumChunkRecoveryInstalled = true;

  window.addEventListener('error', event => {
    // Usually the render path — but non-React code (GrapesJS callbacks, a
    // rethrow in a timer) lands here too, so the handler still checks #root
    // before reloading.
    if (handleChunkLoadError(event.error, true)) event.preventDefault();
  });
  window.addEventListener('unhandledrejection', event => {
    // A rejected import outside render leaves the app mounted and possibly
    // holding unsaved work: never hard-reload from here.
    if (handleChunkLoadError(event.reason, false)) event.preventDefault();
  });
}
